/**
 * Flattens an AgentExecutionNode tree into a list of timeline spans.
 *
 * Span types:
 *   "agent"     – an agent's total wall-clock duration
 *   "tool"      – a tool call within an agent (real timing from event log)
 *   "inference" – an LLM provider call within an agent (real timing from event log)
 */

/**
 * @param {object} rootNode  – AgentExecutionNode tree from the API
 * @returns {{ spans: Array, totalDurationMs: number }}
 */
export function flattenTree(rootNode) {
  const rootStart = new Date(rootNode.startTime).getTime();
  const spans = [];
  let nextId = 0;

  function walk(node, parentSpanId) {
    const nodeStart = new Date(node.startTime).getTime();
    const offset = nodeStart - rootStart;
    const agentSpanId = nextId++;

    spans.push({
      id: agentSpanId,
      label: node.agentRole || "Agent",
      type: "agent",
      depth: node.depth,
      startOffset: offset,
      duration: node.durationMs,
      status: node.status,
      parentId: parentSpanId,
      tokensIn: node.tokensIn,
      tokensOut: node.tokensOut,
      creditsUsed: node.creditsUsed,
      requestId: node.requestId,
      errorMessage: node.errorMessage,
    });

    // Sort tool calls by timestamp
    const sortedTools = [...(node.toolCalls || [])].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );

    for (const tc of sortedTools) {
      const tcStart = new Date(tc.timestamp).getTime();
      const tcOffset = tcStart - rootStart;
      const toolSpanId = nextId++;

      const isAgentSpawn = !!tc.spawnedRequestId;
      const tcStatus =
        tc.status === "failed"
          ? "failed"
          : tc.status === "succeeded"
          ? "completed"
          : tc.durationMs > 0
          ? "completed"
          : "unknown";
      spans.push({
        id: toolSpanId,
        label: tc.toolName,
        type: "tool",
        subType: isAgentSpawn ? "agent-spawn" : toolTypeLabel(tc.toolType),
        depth: node.depth + 1,
        startOffset: tcOffset,
        duration: tc.durationMs,
        status: tcStatus,
        parentId: agentSpanId,
        spawnedRequestId: tc.spawnedRequestId,
      });
    }

    // Real inference spans from the event log (AgentInferenceStarted/Completed pairs)
    const sortedInferences = [...(node.inferenceCalls || [])].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
    );

    for (const inf of sortedInferences) {
      const infStart = new Date(inf.timestamp).getTime();
      const infOffset = infStart - rootStart;
      // Prefer ProviderDurationMs (total provider-side time across the fallback chain — the
      // successful call plus any failed/retried attempts) for the span width; fall back to
      // DurationMs (iteration time, includes router overhead) when missing — that's either
      // a legacy event or a cache hit with no provider call.
      const spanDuration =
        inf.providerDurationMs > 0 ? inf.providerDurationMs : inf.durationMs;
      const overhead =
        inf.providerDurationMs > 0 && inf.durationMs > inf.providerDurationMs
          ? inf.durationMs - inf.providerDurationMs
          : 0;
      spans.push({
        id: nextId++,
        label: inf.wasCached
          ? `Cached (${inf.model || "?"})`
          : inf.model || "LLM Inference",
        type: "inference",
        subType: inf.wasCached ? "cached" : "live",
        depth: node.depth + 1,
        startOffset: infOffset,
        duration: spanDuration,
        status: inf.status === "failed" ? "failed" : "completed",
        parentId: agentSpanId,
        provider: inf.provider,
        wasCached: inf.wasCached,
        providerDurationMs: inf.providerDurationMs,
        iterationDurationMs: inf.durationMs,
        routerOverheadMs: overhead,
      });
    }

    // Recurse into children
    for (const child of node.children || []) {
      walk(child, agentSpanId);
    }
  }

  walk(rootNode, null);

  // Sort spans: agents first (by offset), then their children (by offset)
  spans.sort((a, b) => {
    // Primary: by startOffset
    if (a.startOffset !== b.startOffset) return a.startOffset - b.startOffset;
    // Agent spans before their children
    if (a.type === "agent" && b.type !== "agent") return -1;
    if (a.type !== "agent" && b.type === "agent") return 1;
    return a.id - b.id;
  });

  // Re-order by depth-first tree walk for visual grouping
  const ordered = depthFirstOrder(spans);

  return {
    spans: ordered,
    totalDurationMs: rootNode.durationMs,
  };
}

/**
 * Re-orders spans so children appear directly after their parent (depth-first).
 */
function depthFirstOrder(spans) {
  const byParent = new Map();
  for (const s of spans) {
    const key = s.parentId ?? "__root__";
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(s);
  }

  const result = [];
  function visit(parentId) {
    const key = parentId ?? "__root__";
    const children = byParent.get(key) || [];
    // Sort children: agents first, then by startOffset
    children.sort((a, b) => {
      if (a.type === "agent" && b.type !== "agent") return -1;
      if (a.type !== "agent" && b.type === "agent") return 1;
      return a.startOffset - b.startOffset;
    });
    for (const child of children) {
      result.push(child);
      if (child.type === "agent") {
        visit(child.id);
      }
    }
  }
  visit(null);
  return result;
}

function toolTypeLabel(toolType) {
  // ToolType enum: 0=External, 1=Virtual, 2=Agent, 3=CritiqueLoop
  switch (toolType) {
    case 0:
    case "External":
      return "external";
    case 1:
    case "Virtual":
      return "virtual";
    case 2:
    case "Agent":
      return "agent";
    case 3:
    case "CritiqueLoop":
      return "critique";
    default:
      return "external";
  }
}

/**
 * Returns a color for a span based on its type.
 */
export function spanColor(span) {
  if (span.status === "failed" || span.status === "timeout") return "#fa5252"; // red

  switch (span.type) {
    case "agent":
      return "#4c6ef5"; // indigo
    case "inference":
      return "#748ffc"; // indigo lighter
    case "tool":
      if (span.subType === "agent-spawn") return "#9775fa"; // purple
      if (span.subType === "virtual") return "#20c997"; // teal
      return "#15aabf"; // cyan – external tools
    default:
      return "#868e96"; // gray
  }
}

/**
 * Format milliseconds for display.
 */
export function formatDuration(ms) {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 10_000) return `${(ms / 1000).toFixed(2)}s`;
  return `${(ms / 1000).toFixed(1)}s`;
}
